import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import { Button } from '../UI';
import { 
  ShoppingCart, Plus, Trash2, Check, X, Circle, 
  Archive, Edit2, Store, ChevronRight, StickyNote, User, DollarSign, Save
} from 'lucide-react';
import { useStores } from '../../hooks/useStores';
import { useCareProfiles } from '../../hooks/useCareProfiles';

const GroceryListHub = () => {
  const [lists, setLists] = useState([]);
  const [archivedLists, setArchivedLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showArchivedModal, setShowArchivedModal] = useState(false);
  const [showProfilesModal, setShowProfilesModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  
  // Create modal state
  const [newListName, setNewListName] = useState('');
  const [newListNotes, setNewListNotes] = useState('');
  const [newListStoreId, setNewListStoreId] = useState(null);
  const [newListProfiles, setNewListProfiles] = useState([]);
  
  const [selectedList, setSelectedList] = useState(null);
  const [newItemName, setNewItemName] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [editingListId, setEditingListId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  
  // Editable metadata
  const [editedNotes, setEditedNotes] = useState('');
  const [editedStoreId, setEditedStoreId] = useState(null);
  const [editedProfiles, setEditedProfiles] = useState([]);
  const [totalCost, setTotalCost] = useState('');
  
  const { stores = [] } = useStores();
  const { careProfiles = [] } = useCareProfiles();

  useEffect(() => {
    fetchLists();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedList) {
      setEditedNotes(selectedList.notes || '');
      setEditedStoreId(selectedList.storeId || null);
      setEditedProfiles(selectedList.careProfileIds || []);
      setIsEditingMetadata(false);
    }
  }, [selectedList]);

  const fetchLists = async () => {
    if (!auth.currentUser) return;

    try {
      setLoading(true);
      
      // Fetch active lists
      const activeQuery = query(
        collection(db, 'groceryLists'),
        where('ownerId', '==', auth.currentUser.uid),
        where('isArchived', '==', false),
        orderBy('updatedAt', 'desc')
      );

      const activeSnapshot = await getDocs(activeQuery);
      const activeListsData = activeSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch archived lists
      const archivedQuery = query(
        collection(db, 'groceryLists'),
        where('ownerId', '==', auth.currentUser.uid),
        where('isArchived', '==', true),
        orderBy('updatedAt', 'desc')
      );

      const archivedSnapshot = await getDocs(archivedQuery);
      const archivedListsData = archivedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setLists(activeListsData);
      setArchivedLists(archivedListsData);
      
      if (activeListsData.length > 0 && !selectedList) {
        setSelectedList(activeListsData[0]);
      }
    } catch (error) {
      console.error('Error fetching grocery lists:', error);
    } finally {
      setLoading(false);
    }
  };

  const createList = async () => {
    if (!auth.currentUser || !newListName.trim()) return;

    try {
      const storeName = newListStoreId ? stores.find(s => s.id === newListStoreId)?.name : null;

      const newList = {
        name: newListName.trim(),
        ownerId: auth.currentUser.uid,
        items: [],
        sharedWith: [],
        isArchived: false,
        storeId: newListStoreId,
        storeName: storeName,
        notes: newListNotes.trim(),
        careProfileIds: newListProfiles,
        totalCost: null,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'groceryLists'), newList);
      
      const createdList = {
        id: docRef.id,
        ...newList
      };

      setLists(prev => [createdList, ...prev]);
      setSelectedList(createdList);
      
      // Reset form
      setNewListName('');
      setNewListNotes('');
      setNewListStoreId(null);
      setNewListProfiles([]);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating list:', error);
      alert('Failed to create list');
    }
  };

  const startEditingMetadata = () => {
    setIsEditingMetadata(true);
  };

  const cancelEditingMetadata = () => {
    setEditedNotes(selectedList.notes || '');
    setEditedStoreId(selectedList.storeId || null);
    setEditedProfiles(selectedList.careProfileIds || []);
    setIsEditingMetadata(false);
  };

  const saveListMetadata = async () => {
    if (!selectedList) return;

    try {
      const storeName = editedStoreId ? stores.find(s => s.id === editedStoreId)?.name : null;

      await updateDoc(doc(db, 'groceryLists', selectedList.id), {
        notes: editedNotes,
        storeId: editedStoreId,
        storeName: storeName,
        careProfileIds: editedProfiles,
        updatedAt: new Date()
      });

      const updatedList = {
        ...selectedList,
        notes: editedNotes,
        storeId: editedStoreId,
        storeName: storeName,
        careProfileIds: editedProfiles
      };

      setSelectedList(updatedList);
      setLists(prev => prev.map(list => 
        list.id === selectedList.id ? updatedList : list
      ));
      setIsEditingMetadata(false);
    } catch (error) {
      console.error('Error saving metadata:', error);
      alert('Failed to save changes');
    }
  };

  const renameList = async (listId, newName) => {
    if (!newName.trim()) return;

    try {
      await updateDoc(doc(db, 'groceryLists', listId), {
        name: newName.trim(),
        updatedAt: new Date()
      });

      setLists(prev => prev.map(list => 
        list.id === listId ? { ...list, name: newName.trim() } : list
      ));
      
      if (selectedList?.id === listId) {
        setSelectedList({ ...selectedList, name: newName.trim() });
      }

      setEditingListId(null);
      setEditingName('');
    } catch (error) {
      console.error('Error renaming list:', error);
    }
  };

  const archiveListWithCost = async () => {
    if (!selectedList) return;

    try {
      const cost = totalCost ? parseFloat(totalCost) : null;

      await updateDoc(doc(db, 'groceryLists', selectedList.id), {
        isArchived: true,
        totalCost: cost,
        archivedAt: new Date(),
        updatedAt: new Date()
      });

      const listToArchive = lists.find(l => l.id === selectedList.id);
      setLists(prev => prev.filter(l => l.id !== selectedList.id));
      if (listToArchive) {
        setArchivedLists(prev => [{ ...listToArchive, isArchived: true, totalCost: cost }, ...prev]);
      }

      setSelectedList(lists[0] || null);
      setShowArchiveModal(false);
      setTotalCost('');
    } catch (error) {
      console.error('Error archiving list:', error);
      alert('Failed to archive list');
    }
  };

  const unarchiveList = async (listId) => {
    try {
      await updateDoc(doc(db, 'groceryLists', listId), {
        isArchived: false,
        updatedAt: new Date()
      });

      fetchLists();
    } catch (error) {
      console.error('Error unarchiving list:', error);
    }
  };

  const deleteList = async (listId) => {
    if (!window.confirm('Delete this list permanently?')) return;

    try {
      await deleteDoc(doc(db, 'groceryLists', listId));
      setArchivedLists(prev => prev.filter(l => l.id !== listId));
    } catch (error) {
      console.error('Error deleting list:', error);
    }
  };

  const addItemToList = async () => {
    if (!selectedList || !newItemName.trim()) return;

    try {
      const newItem = {
        id: `${Date.now()}_${Math.random()}`,
        name: newItemName.trim(),
        quantity: 1,
        category: 'other',
        isChecked: false,
        addedAt: new Date()
      };

      const updatedItems = [...(selectedList.items || []), newItem];

      await updateDoc(doc(db, 'groceryLists', selectedList.id), {
        items: updatedItems,
        updatedAt: new Date()
      });

      setSelectedList({ ...selectedList, items: updatedItems });
      setNewItemName('');
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const toggleItemCheck = async (itemId) => {
    if (!selectedList) return;

    try {
      const updatedItems = selectedList.items.map(item =>
        item.id === itemId ? { ...item, isChecked: !item.isChecked } : item
      );

      await updateDoc(doc(db, 'groceryLists', selectedList.id), {
        items: updatedItems,
        updatedAt: new Date()
      });

      setSelectedList({ ...selectedList, items: updatedItems });
    } catch (error) {
      console.error('Error toggling item:', error);
    }
  };

  const removeItem = async (itemId) => {
    if (!selectedList) return;

    try {
      const updatedItems = selectedList.items.filter(item => item.id !== itemId);

      await updateDoc(doc(db, 'groceryLists', selectedList.id), {
        items: updatedItems,
        updatedAt: new Date()
      });

      setSelectedList({ ...selectedList, items: updatedItems });
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  const clearCheckedItems = async () => {
    if (!selectedList) return;

    try {
      const updatedItems = selectedList.items.filter(item => !item.isChecked);

      await updateDoc(doc(db, 'groceryLists', selectedList.id), {
        items: updatedItems,
        updatedAt: new Date()
      });

      setSelectedList({ ...selectedList, items: updatedItems });
      setShowClearConfirm(false);
    } catch (error) {
      console.error('Error clearing items:', error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-xxxl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto mb-md"></div>
        <p className="text-callout text-text-secondary">Loading your lists...</p>
      </div>
    );
  }

  const items = selectedList?.items || [];
  const uncheckedItems = items.filter(item => !item.isChecked);
  const checkedItems = items.filter(item => item.isChecked);
  const progress = items.length > 0 ? (checkedItems.length / items.length) * 100 : 0;
  const currentStore = (isEditingMetadata ? editedStoreId : selectedList?.storeId) 
    ? stores.find(s => s.id === (isEditingMetadata ? editedStoreId : selectedList?.storeId)) 
    : null;
  const listProfiles = careProfiles.filter(p => 
    (isEditingMetadata ? editedProfiles : selectedList?.careProfileIds || []).includes(p.id)
  );

  const newListSelectedProfiles = careProfiles.filter(p => newListProfiles.includes(p.id));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-lg">
      {/* Main List - 2 columns */}
      <div className="lg:col-span-2 space-y-lg">
        {!selectedList ? (
          <div className="p-xl rounded-large bg-gradient-to-br from-surface to-primary/5 border border-text-tertiary/10 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-lg">
              <ShoppingCart size={36} className="text-primary" />
            </div>
            <h3 className="text-title-3 text-primary mb-md">No Lists Yet</h3>
            <p className="text-body text-text-secondary mb-lg">
              Create your first shopping list to get started
            </p>
            <Button
              variant="primary"
              icon={<Plus size={18} />}
              onClick={() => setShowCreateModal(true)}
            >
              Create List
            </Button>
          </div>
        ) : (
          <>
            {/* Header with metadata */}
            <div className="p-lg rounded-large border border-text-tertiary/10 bg-surface space-y-md">
              {/* List Name */}
              <div className="flex items-start justify-between">
                {editingListId === selectedList.id ? (
                  <div className="flex items-center gap-sm flex-1">
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && renameList(selectedList.id, editingName)}
                      className="flex-1 px-md py-sm bg-surface border border-primary rounded-medium focus:outline-none text-title-3 font-rounded text-primary"
                      autoFocus
                    />
                    <button
                      onClick={() => renameList(selectedList.id, editingName)}
                      className="w-8 h-8 rounded-full hover:bg-success/10 flex items-center justify-center"
                    >
                      <Check size={16} className="text-success" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingListId(null);
                        setEditingName('');
                      }}
                      className="w-8 h-8 rounded-full hover:bg-error/10 flex items-center justify-center"
                    >
                      <X size={16} className="text-error" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex-1">
                      <h1 className="text-title-2 font-rounded text-primary mb-xs">{selectedList.name}</h1>
                      <p className="text-caption text-text-secondary">
                        {items.length} item{items.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-sm">
                      <button
                        onClick={() => {
                          setEditingListId(selectedList.id);
                          setEditingName(selectedList.name);
                        }}
                        className="w-8 h-8 rounded-full hover:bg-primary/10 flex items-center justify-center"
                      >
                        <Edit2 size={16} className="text-primary" />
                      </button>
                      {!isEditingMetadata && (
                        <button
                          onClick={startEditingMetadata}
                          className="px-md py-sm rounded-medium border border-text-tertiary/20 hover:border-primary/30 transition-all flex items-center gap-xs"
                        >
                          <Edit2 size={14} className="text-primary" />
                          <span className="text-caption text-primary">Edit Details</span>
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Static View */}
              {!isEditingMetadata ? (
                <>
                  {/* Description */}
                  {selectedList.notes && (
                    <div className="p-md rounded-medium bg-gradient-to-br from-surface to-primary/5 border border-text-tertiary/10">
                      <div className="flex items-start gap-sm">
                        <StickyNote size={14} className="text-primary mt-1 flex-shrink-0" />
                        <p className="text-caption text-text-secondary whitespace-pre-wrap flex-1">
                          {selectedList.notes}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Store & Profiles */}
                  <div className="flex items-center gap-sm flex-wrap">
                    {currentStore && (
                      <div
                        className="flex items-center gap-sm px-md py-sm rounded-full"
                        style={{ backgroundColor: `${currentStore.color}10`, borderColor: `${currentStore.color}30` }}
                      >
                        <Store size={14} style={{ color: currentStore.color }} />
                        <span className="text-caption font-semibold" style={{ color: currentStore.color }}>
                          {currentStore.name}
                        </span>
                      </div>
                    )}

                    {listProfiles.map(profile => (
                      <div
                        key={profile.id}
                        className="flex items-center gap-xs px-md py-sm rounded-full bg-accent/10 border border-accent/20"
                      >
                        <User size={12} className="text-accent" />
                        <span className="text-caption font-semibold text-accent">{profile.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                /* Edit View */
                <>
                  {/* Description/Notes */}
                  <div>
                    <label className="text-caption text-text-secondary mb-xs block">Description</label>
                    <textarea
                      placeholder="Add a description for this list..."
                      value={editedNotes}
                      onChange={(e) => setEditedNotes(e.target.value)}
                      className="w-full px-md py-sm bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-callout resize-none"
                      rows={2}
                    />
                  </div>

                  {/* Store Selection */}
                  <div>
                    <label className="text-caption text-text-secondary mb-xs block">Store</label>
                    <div className="relative">
                      <select
                        value={editedStoreId || ''}
                        onChange={(e) => setEditedStoreId(e.target.value || null)}
                        className="w-full px-md py-sm bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-callout appearance-none pr-10"
                      >
                        <option value="">No store selected</option>
                        {stores.map(store => (
                          <option key={store.id} value={store.id}>{store.name}</option>
                        ))}
                      </select>
                      <ChevronRight size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary rotate-90 pointer-events-none" />
                    </div>
                  </div>

                  {/* Care Profiles */}
                  <div>
                    <label className="text-caption text-text-secondary mb-xs block">Care Profiles</label>
                    <button
                      onClick={() => setShowProfilesModal(true)}
                      className="w-full px-md py-sm bg-surface border border-text-tertiary/20 rounded-medium hover:border-primary/30 transition-all text-left flex items-center justify-between"
                    >
                      <div className="flex items-center gap-sm flex-wrap">
                        {listProfiles.length === 0 ? (
                          <span className="text-callout text-text-tertiary">No profiles tagged</span>
                        ) : (
                          listProfiles.map(profile => (
                            <div
                              key={profile.id}
                              className="flex items-center gap-xs px-sm py-xs rounded-full bg-accent/10 border border-accent/20"
                            >
                              <User size={12} className="text-accent" />
                              <span className="text-caption font-semibold text-accent">{profile.name}</span>
                            </div>
                          ))
                        )}
                      </div>
                      <Plus size={16} className="text-primary flex-shrink-0" />
                    </button>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-md pt-md border-t border-text-tertiary/10">
                    <Button
                      variant="outline"
                      onClick={cancelEditingMetadata}
                      className="flex-1"
                      size="small"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      icon={<Save size={16} />}
                      onClick={saveListMetadata}
                      className="flex-1"
                      size="small"
                    >
                      Save Changes
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Progress */}
            {items.length > 0 && (
              <div className="p-lg rounded-large bg-gradient-to-br from-primary/5 to-accent/5 border border-text-tertiary/10">
                <div className="flex items-center justify-between mb-sm">
                  <span className="text-callout font-semibold text-primary">
                    {checkedItems.length} of {items.length}
                  </span>
                  <span className="text-callout text-text-secondary">{Math.round(progress)}%</span>
                </div>
                
                <div className="w-full h-3 bg-surface rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                {checkedItems.length > 0 && (
                  <div className="mt-md pt-md border-t border-text-tertiary/10">
                    {!showClearConfirm ? (
                      <button
                        onClick={() => setShowClearConfirm(true)}
                        className="text-caption text-text-secondary hover:text-error transition-colors flex items-center gap-xs"
                      >
                        <Trash2 size={14} />
                        Clear {checkedItems.length} checked
                      </button>
                    ) : (
                      <div className="flex items-center gap-md">
                        <p className="text-caption text-text-secondary flex-1">Remove checked?</p>
                        <Button variant="outline" size="small" onClick={() => setShowClearConfirm(false)}>
                          Cancel
                        </Button>
                        <Button variant="primary" size="small" onClick={clearCheckedItems}>
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Add Item */}
            <div className="p-md rounded-large border border-text-tertiary/10">
              <div className="flex gap-sm">
                <input
                  type="text"
                  placeholder="Add item..."
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addItemToList()}
                  className="flex-1 px-md py-sm bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-callout placeholder:text-text-tertiary"
                />
                <Button
                  variant="primary"
                  size="small"
                  icon={<Plus size={16} />}
                  onClick={addItemToList}
                  disabled={!newItemName.trim()}
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Items */}
            <div className="space-y-sm">
              {uncheckedItems.map(item => (
                <div 
                  key={item.id}
                  className="flex items-center gap-md p-md rounded-large border border-text-tertiary/10 bg-gradient-to-br from-surface to-primary/5 hover:border-primary/20 transition-all"
                >
                  <button
                    onClick={() => toggleItemCheck(item.id)}
                    className="w-6 h-6 rounded-full border-2 border-text-tertiary/30 hover:border-primary transition-colors flex items-center justify-center flex-shrink-0"
                  >
                    <Circle size={12} className="text-text-tertiary" />
                  </button>

                  {item.productImageURL && (
                    <div className="w-12 h-12 rounded-medium bg-surface flex items-center justify-center overflow-hidden flex-shrink-0">
                      <img 
                        src={item.productImageURL} 
                        alt={item.name}
                        className="w-full h-full object-contain p-xs"
                      />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-callout font-semibold text-primary truncate">{item.name}</p>
                    {item.quantity > 1 && (
                      <p className="text-caption text-text-tertiary">Qty: {item.quantity}</p>
                    )}
                  </div>

                  <button
                    onClick={() => removeItem(item.id)}
                    className="w-8 h-8 rounded-full hover:bg-error/10 flex items-center justify-center text-text-tertiary hover:text-error transition-colors flex-shrink-0"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}

              {checkedItems.length > 0 && uncheckedItems.length > 0 && (
                <div className="h-px bg-text-tertiary/10 my-md" />
              )}

              {checkedItems.map(item => (
                <div 
                  key={item.id}
                  className="flex items-center gap-md p-md rounded-large border border-text-tertiary/5 bg-surface opacity-60"
                >
                  <button
                    onClick={() => toggleItemCheck(item.id)}
                    className="w-6 h-6 rounded-full bg-success flex items-center justify-center flex-shrink-0"
                  >
                    <Check size={14} className="text-white" />
                  </button>

                  {item.productImageURL && (
                    <div className="w-12 h-12 rounded-medium bg-surface flex items-center justify-center overflow-hidden flex-shrink-0 grayscale">
                      <img 
                        src={item.productImageURL} 
                        alt={item.name}
                        className="w-full h-full object-contain p-xs"
                      />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-callout text-text-secondary line-through truncate">{item.name}</p>
                  </div>

                  <button
                    onClick={() => removeItem(item.id)}
                    className="w-8 h-8 rounded-full hover:bg-error/10 flex items-center justify-center text-text-tertiary hover:text-error transition-colors flex-shrink-0"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}

              {items.length === 0 && (
                <div className="p-xl rounded-large bg-gradient-to-br from-surface to-primary/5 border border-text-tertiary/10 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-md">
                    <ShoppingCart size={28} className="text-primary" />
                  </div>
                  <h3 className="text-callout text-primary mb-xs">List is empty</h3>
                  <p className="text-caption text-text-secondary">Add items to start shopping</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Sidebar - 1 column */}
      <div className="space-y-lg">
        {/* Actions */}
        <div className="p-lg rounded-large border border-text-tertiary/10 bg-surface space-y-sm">
          <h3 className="text-callout font-semibold text-primary mb-md">Actions</h3>
          
          <Button
            variant="outline"
            icon={<Plus size={16} />}
            onClick={() => setShowCreateModal(true)}
            className="w-full"
            size="small"
          >
            New List
          </Button>

          {selectedList && (
            <Button
              variant="outline"
              icon={<Archive size={16} />}
              onClick={() => setShowArchiveModal(true)}
              className="w-full"
              size="small"
            >
              Archive List
            </Button>
          )}

          <Button
            variant="outline"
            icon={<Archive size={16} />}
            onClick={() => setShowArchivedModal(true)}
            className="w-full"
            size="small"
          >
            Archived ({archivedLists.length})
          </Button>
        </div>

        {/* All Lists */}
        <div className="p-lg rounded-large border border-text-tertiary/10 bg-surface">
          <h3 className="text-callout font-semibold text-primary mb-md">All Lists</h3>
          <div className="space-y-xs">
            {lists.map(list => (
              <button
                key={list.id}
                onClick={() => setSelectedList(list)}
                className={`w-full text-left p-md rounded-medium transition-all flex items-center justify-between ${
                  selectedList?.id === list.id
                    ? 'bg-primary/10 border border-primary/20'
                    : 'hover:bg-surface'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-caption font-semibold text-primary truncate">{list.name}</p>
                  <p className="text-footnote text-text-tertiary">{list.items?.length || 0} items</p>
                </div>
                <ChevronRight size={14} className="text-text-tertiary flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Create List Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-lg">
          <div className="bg-background p-xl rounded-large border border-text-tertiary/20 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-title-2 font-rounded text-primary mb-lg">Create New List</h2>
            
            {/* List Name */}
            <div className="mb-lg">
              <label className="text-caption text-text-secondary mb-xs block">List Name</label>
              <input
                type="text"
                placeholder="e.g., Weekly Groceries"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                className="w-full px-lg py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body"
                autoFocus
              />
            </div>

            {/* Description */}
            <div className="mb-lg">
              <label className="text-caption text-text-secondary mb-xs block">Description (Optional)</label>
              <textarea
                placeholder="Add notes about this list..."
                value={newListNotes}
                onChange={(e) => setNewListNotes(e.target.value)}
                className="w-full px-lg py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body resize-none"
                rows={2}
              />
            </div>

            {/* Store */}
            <div className="mb-lg">
              <label className="text-caption text-text-secondary mb-xs block">Store (Optional)</label>
              <div className="relative">
                <select
                  value={newListStoreId || ''}
                  onChange={(e) => setNewListStoreId(e.target.value || null)}
                  className="w-full px-lg py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body appearance-none pr-10"
                >
                  <option value="">No store selected</option>
                  {stores.map(store => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </select>
                <ChevronRight size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary rotate-90 pointer-events-none" />
              </div>
            </div>

            {/* Care Profiles */}
            <div className="mb-lg">
              <label className="text-caption text-text-secondary mb-xs block">Care Profiles (Optional)</label>
              <div className="space-y-sm max-h-48 overflow-y-auto p-sm border border-text-tertiary/10 rounded-medium bg-surface">
                {careProfiles.length === 0 ? (
                  <p className="text-center text-caption text-text-tertiary py-md">
                    No care profiles available
                  </p>
                ) : (
                  careProfiles.map(profile => {
                    const isSelected = newListProfiles.includes(profile.id);
                    return (
                      <button
                        key={profile.id}
                        onClick={() => {
                          if (isSelected) {
                            setNewListProfiles(prev => prev.filter(id => id !== profile.id));
                          } else {
                            setNewListProfiles(prev => [...prev, profile.id]);
                          }
                        }}
                        className={`w-full p-sm rounded-medium border transition-all text-left flex items-center gap-sm ${
                          isSelected
                            ? 'border-accent/30 bg-accent/10'
                            : 'border-text-tertiary/10 hover:border-accent/20'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? 'border-accent bg-accent'
                            : 'border-text-tertiary/30'
                        }`}>
                          {isSelected && <Check size={12} className="text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-caption font-semibold text-primary truncate">{profile.name}</p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Selected Profiles Preview */}
            {newListSelectedProfiles.length > 0 && (
              <div className="mb-lg flex items-center gap-sm flex-wrap">
                {newListSelectedProfiles.map(profile => (
                  <div
                    key={profile.id}
                    className="flex items-center gap-xs px-sm py-xs rounded-full bg-accent/10 border border-accent/20"
                  >
                    <User size={12} className="text-accent" />
                    <span className="text-caption font-semibold text-accent">{profile.name}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-md justify-end">
              <Button variant="outline" onClick={() => {
                setShowCreateModal(false);
                setNewListName('');
                setNewListNotes('');
                setNewListStoreId(null);
                setNewListProfiles([]);
              }}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => { createList(); }} disabled={!newListName.trim()}>
                Create List
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Selection Modal (for editing) - same as before */}
      {showProfilesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-lg">
          <div className="bg-background p-xl rounded-large border border-text-tertiary/20 max-w-md w-full">
            <h2 className="text-title-2 font-rounded text-primary mb-lg">Tag Care Profiles</h2>
            <p className="text-caption text-text-secondary mb-lg">
              Filter products based on selected profiles' dietary restrictions
            </p>
            <div className="space-y-sm mb-lg max-h-96 overflow-y-auto">
              {careProfiles.length === 0 ? (
                <p className="text-center text-caption text-text-secondary py-lg">
                  No care profiles yet. Create one in Dashboard.
                </p>
              ) : (
                careProfiles.map(profile => {
                  const isSelected = editedProfiles.includes(profile.id);
                  return (
                    <button
                      key={profile.id}
                      onClick={() => {
                        if (isSelected) {
                          setEditedProfiles(prev => prev.filter(id => id !== profile.id));
                        } else {
                          setEditedProfiles(prev => [...prev, profile.id]);
                        }
                      }}
                      className={`w-full p-md rounded-medium border transition-all text-left flex items-center gap-md ${
                        isSelected
                          ? 'border-accent/30 bg-accent/10'
                          : 'border-text-tertiary/10 hover:border-accent/20'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected
                          ? 'border-accent bg-accent'
                          : 'border-text-tertiary/30'
                      }`}>
                        {isSelected && <Check size={14} className="text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-callout font-semibold text-primary">{profile.name}</p>
                        {profile.dietaryTags && profile.dietaryTags.length > 0 && (
                          <p className="text-caption text-text-secondary truncate">
                            {profile.dietaryTags.slice(0, 3).join(', ')}
                            {profile.dietaryTags.length > 3 && ` +${profile.dietaryTags.length - 3}`}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            <div className="flex gap-md justify-end">
              <Button variant="outline" onClick={() => setShowProfilesModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-lg">
          <div className="bg-background p-xl rounded-large border border-text-tertiary/20 max-w-md w-full">
            <h2 className="text-title-2 font-rounded text-primary mb-lg">Archive List</h2>
            <p className="text-caption text-text-secondary mb-lg">
              Track how much you spent on this trip (optional)
            </p>
            <div className="mb-lg">
              <label className="text-caption text-text-secondary mb-xs block">Total Cost</label>
              <div className="relative">
                <DollarSign size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={totalCost}
                  onChange={(e) => setTotalCost(e.target.value)}
                  className="w-full pl-10 pr-lg py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body"
                />
              </div>
            </div>
            <div className="flex gap-md justify-end">
              <Button variant="outline" onClick={() => {
                setShowArchiveModal(false);
                setTotalCost('');
              }}>
                Cancel
              </Button>
              <Button variant="primary" onClick={archiveListWithCost}>
                Archive
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Archived Lists Modal */}
      {showArchivedModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-lg">
          <div className="bg-background p-xl rounded-large border border-text-tertiary/20 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h2 className="text-title-2 font-rounded text-primary mb-lg">Archived Lists</h2>
            <div className="space-y-sm">
              {archivedLists.length === 0 ? (
                <p className="text-center text-caption text-text-secondary py-xl">No archived lists</p>
              ) : (
                archivedLists.map(list => (
                  <div key={list.id} className="p-lg rounded-medium border border-text-tertiary/10 flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-callout font-semibold text-primary">{list.name}</p>
                      <div className="flex items-center gap-md mt-xs">
                        <p className="text-caption text-text-secondary">{list.items?.length || 0} items</p>
                        {list.totalCost && (
                          <div className="flex items-center gap-xs">
                            <DollarSign size={12} className="text-success" />
                            <p className="text-caption font-semibold text-success">
                              {list.totalCost.toFixed(2)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-sm">
                      <Button variant="outline" size="small" onClick={() => unarchiveList(list.id)}>
                        Unarchive
                      </Button>
                      <Button variant="outline" size="small" icon={<Trash2 size={14} />} onClick={() => deleteList(list.id)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="mt-lg">
              <Button variant="outline" onClick={() => setShowArchivedModal(false)} className="w-full">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroceryListHub;
